// Cabinet validation — checks drillings + part geometry for shop-floor
// problems before they reach the saw / CNC. Pure functions; no React,
// no fetch. Consumers (PartEditor, CabinetEditor, future Problems panel)
// call into the validator and surface Issues.
//
// Severity:
//   error   — will produce a broken panel (hole outside the part, depth
//             larger than thickness, two holes overlapping). Block export.
//   warning — likely a mistake but the saw will still produce a part
//             (mirror asymmetry, sparse shelf-pin row). Render but warn.

import type { Cabinet, CabinetPart, Id } from "@/lib/types";
import type { CatalogIndex } from "./bom";
import { partsFor, effectiveDrillings } from "./bom";
import type { DrillEntry } from "./index";
import {
  isDoorPart,
  isShelfPart,
  isSidePart,
  isLeftSidePart,
  isRightSidePart,
  isStretcherPart,
  isBottomPart,
} from "./roles";

export type IssueSeverity = "error" | "warning";

export interface Issue {
  severity: IssueSeverity;
  /** Stable code — useful for filtering / batch suppression in future. */
  code:
    | "hole_outside_panel"
    | "hole_depth_exceeds_thickness"
    | "holes_overlap"
    | "mirror_asymmetry"
    | "hinge_cup_outside_door"
    | "cabinet_classid_unknown"
    | "joint_missing_screws"
    | "joint_height_mismatch"
    ;
  /** Human-readable message — surfaced in PartEditor / tooltips. */
  message: string;
  /** Which part the issue belongs to (omitted on cabinet-wide issues). */
  partId?: string;
  /** Index of the offending drilling inside effectiveDrillings(cab, partId). */
  drillingIndex?: number;
  /** Cabinet the issue belongs to (set by validateProject; per-cabinet
   *  callers can leave it undefined). For joint issues, the "owner"
   *  cabinet — `otherCabinetId` carries the neighbour. */
  cabinetId?: Id;
  /** Adjacent cabinet on cross-cabinet joint issues. */
  otherCabinetId?: Id;
}

// ── Single-drilling checks ────────────────────────────────────────

/** Minimum hole-edge-to-panel-edge clearance, inches. Keep tight — 2mm
 *  is the practical lower bound where the drill bit won't tear the
 *  laminate. Hinge cups are intentionally near the door edge (22mm
 *  centre / ~4.5mm edge clearance for ⌀35mm) so anything larger here
 *  would false-positive on the standard 32mm system. */
const PANEL_MARGIN = 2 / 25.4;
/** Two holes are "overlapping" if their centres are closer than
 *  (r1 + r2 + clearance). 1mm clearance. */
const HOLE_CLEARANCE = 1 / 25.4;
/** Drilling depth can equal thickness for through-holes (cable etc.)
 *  but not exceed it. */
const DEPTH_TOLERANCE = 0.001;

function checkOneDrilling(
  part: CabinetPart,
  d: DrillEntry,
  idx: number,
): Issue[] {
  const out: Issue[] = [];
  const r = d.diameter / 2;
  if (d.x - r < PANEL_MARGIN || d.x + r > part.w - PANEL_MARGIN
   || d.y - r < PANEL_MARGIN || d.y + r > part.h - PANEL_MARGIN) {
    out.push({
      severity: "error",
      code: "hole_outside_panel",
      message: `Hole #${idx + 1} (⌀${(d.diameter * 25.4).toFixed(1)}mm) at (${d.x.toFixed(2)}″, ${d.y.toFixed(2)}″) is within ${(PANEL_MARGIN * 25.4).toFixed(0)}mm of the edge or off-panel.`,
      partId: part.id,
      drillingIndex: idx,
    });
  }
  if (d.depth > part.thickness + DEPTH_TOLERANCE) {
    out.push({
      severity: "error",
      code: "hole_depth_exceeds_thickness",
      message: `Hole #${idx + 1} depth ${(d.depth * 25.4).toFixed(1)}mm exceeds panel thickness ${(part.thickness * 25.4).toFixed(1)}mm. Use kind=cable for through-holes.`,
      partId: part.id,
      drillingIndex: idx,
    });
  }
  return out;
}

function checkDrillingOverlaps(
  part: CabinetPart,
  drillings: DrillEntry[],
): Issue[] {
  const out: Issue[] = [];
  for (let i = 0; i < drillings.length; i++) {
    for (let j = i + 1; j < drillings.length; j++) {
      const a = drillings[i]!, b = drillings[j]!;
      if (a.face !== b.face) continue;             // different faces — fine
      const dx = a.x - b.x, dy = a.y - b.y;
      const dist2 = dx * dx + dy * dy;
      const r = a.diameter / 2 + b.diameter / 2 + HOLE_CLEARANCE;
      if (dist2 < r * r) {
        out.push({
          severity: "error",
          code: "holes_overlap",
          message: `Holes #${i + 1} and #${j + 1} on ${part.name} overlap (centres ${(Math.sqrt(dist2) * 25.4).toFixed(1)}mm apart, need ≥ ${(r * 25.4).toFixed(1)}mm).`,
          partId: part.id,
          drillingIndex: i,
        });
      }
    }
  }
  return out;
}

// ── Cross-part / joint checks ─────────────────────────────────────

/** Shelf-pin rows on the left side and right side should mirror at the
 *  same Y positions — a shelf rests on aligned pins. Warn (not error)
 *  because the shop floor can still drill mismatched rows; the shelf
 *  just won't sit flat. */
function checkMirrorAsymmetry(
  cab: Cabinet,
  parts: CabinetPart[],
): Issue[] {
  const left  = parts.find(isLeftSidePart);
  const right = parts.find(isRightSidePart);
  if (!left || !right) return [];

  const lD = effectiveDrillings(cab, left.id).filter((d) => d.kind === "shelf_pin");
  const rD = effectiveDrillings(cab, right.id).filter((d) => d.kind === "shelf_pin");

  // Mirror predicate: a hole on the left at Y has a matching hole on the
  // right at the SAME Y (within 0.5mm). Inverse also.
  const tol = 0.5 / 25.4;
  const ys = (xs: DrillEntry[]) => xs.map((d) => d.y).sort((a, b) => a - b);
  const lY = ys(lD), rY = ys(rD);
  const matched = (target: number[], probe: number[]) =>
    probe.every((y) => target.some((ty) => Math.abs(ty - y) <= tol));

  const issues: Issue[] = [];
  if (!matched(rY, lY)) {
    issues.push({
      severity: "warning",
      code: "mirror_asymmetry",
      message: `Left side has ${lD.length} shelf-pin holes; right side has ${rD.length}. Some Y positions don't match — shelves may sit at an angle.`,
      partId: left.id,
    });
  }
  return issues;
}

/** Hinge cup Y must fall inside the door, not within 16mm of top/bottom
 *  edge of the door (otherwise the hinge plate won't have room). */
function checkHingeCups(part: CabinetPart, drillings: DrillEntry[]): Issue[] {
  if (!isDoorPart(part)) return [];
  const issues: Issue[] = [];
  const minEdge = 16 / 25.4;
  drillings.forEach((d, i) => {
    if (d.kind !== "hinge_cup") return;
    if (d.y < minEdge || d.y > part.h - minEdge) {
      issues.push({
        severity: "error",
        code: "hinge_cup_outside_door",
        message: `Hinge cup at Y=${d.y.toFixed(2)}″ is within ${(minEdge * 25.4).toFixed(0)}mm of the door edge — the hinge plate won't fit.`,
        partId: part.id,
        drillingIndex: i,
      });
    }
  });
  return issues;
}

// ── Top-level ─────────────────────────────────────────────────────

/** Validate a single cabinet end-to-end. Returns every issue found —
 *  callers filter by partId/severity for their UI. */
export function validateCabinet(cab: Cabinet, catalog: CatalogIndex): Issue[] {
  const issues: Issue[] = [];
  const parts = partsFor(cab, catalog);
  if (parts.length === 0) {
    issues.push({
      severity: "error",
      code: "cabinet_classid_unknown",
      message: `Cabinet class "${cab.classId}" is not registered; no parts to validate.`,
    });
    return issues;
  }

  for (const p of parts) {
    const drillings = effectiveDrillings(cab, p.id);
    for (let i = 0; i < drillings.length; i++) {
      issues.push(...checkOneDrilling(p, drillings[i]!, i));
    }
    issues.push(...checkDrillingOverlaps(p, drillings));
    issues.push(...checkHingeCups(p, drillings));
  }

  issues.push(...checkMirrorAsymmetry(cab, parts));

  return issues;
}

// ── Cross-cabinet / joint checks ──────────────────────────────────

/** Two cabinets are "adjacent" on their X-faces if (a) they share the
 *  same anchor (floor-vs-wall don't bolt to each other), (b) their
 *  rotation is the same (we don't try to reason about rotated joints in
 *  Phase 5.1), (c) their bounding boxes touch within `gapTol` along X,
 *  and (d) they overlap in Z by at least 75% of the shallower depth so
 *  a freestanding cabinet edge-to-edge with an island isn't flagged. */
interface AdjacentPair {
  /** Cabinet whose right side touches `b`'s left side. */
  a: Cabinet;
  b: Cabinet;
}

const ADJ_GAP_TOL = 0.25;          // inches — within 6mm
const ADJ_Z_OVERLAP_RATIO = 0.75;

function adjacentPairs(cabinets: Cabinet[]): AdjacentPair[] {
  const pairs: AdjacentPair[] = [];
  for (const a of cabinets) {
    for (const b of cabinets) {
      if (a._id === b._id) continue;
      if (a.anchor !== b.anchor) continue;
      if ((a.rotation || 0) !== (b.rotation || 0)) continue;
      // a.right at x = a.position[0] + a.width.  b.left at b.position[0].
      const aRight = a.position[0] + a.width;
      const bLeft  = b.position[0];
      if (Math.abs(bLeft - aRight) > ADJ_GAP_TOL) continue;
      // Z overlap
      const aZ0 = a.position[2], aZ1 = a.position[2] + a.depth;
      const bZ0 = b.position[2], bZ1 = b.position[2] + b.depth;
      const z0 = Math.max(aZ0, bZ0), z1 = Math.min(aZ1, bZ1);
      const overlap = z1 - z0;
      const minDepth = Math.min(a.depth, b.depth);
      if (overlap < minDepth * ADJ_Z_OVERLAP_RATIO) continue;
      pairs.push({ a, b });
    }
  }
  return pairs;
}

function checkJoint(pair: AdjacentPair, catalog: CatalogIndex): Issue[] {
  const { a, b } = pair;
  const out: Issue[] = [];

  // 1. Height mismatch — floor cabinets should share their carcass-top
  // (otherwise a continuous countertop won't sit flat). Wall cabinets
  // typically also share a top line for the eye.
  if (Math.abs(a.height - b.height) > 0.125) {
    out.push({
      severity: "warning",
      code: "joint_height_mismatch",
      message: `Adjacent cabinets #${a._id} and #${b._id} differ in height by ${(a.height - b.height).toFixed(2)}″ — countertop / cornice will step.`,
      cabinetId: a._id,
      otherCabinetId: b._id,
    });
  }

  // 2. Missing screw drillings — the touching sides need a pair of
  // through-screws so the carcasses bolt together. Look up the side
  // panels by role (user-authored classes may use any id), then check
  // each for any drilling with kind=screw.
  const aParts = partsFor(a, catalog);
  const bParts = partsFor(b, catalog);
  const aRight = aParts.find(isRightSidePart);
  const bLeft  = bParts.find(isLeftSidePart);
  const aRightScrews = aRight ? effectiveDrillings(a, aRight.id).filter((d) => d.kind === "screw").length : 0;
  const bLeftScrews  = bLeft  ? effectiveDrillings(b, bLeft.id ).filter((d) => d.kind === "screw").length : 0;
  if (aRightScrews === 0 && bLeftScrews === 0) {
    out.push({
      severity: "warning",
      code: "joint_missing_screws",
      message: `Cabinets #${a._id} ↔ #${b._id} touch but neither has screw drillings on the mating sides — assemblers will free-drill on the floor.`,
      cabinetId: a._id,
      otherCabinetId: b._id,
    });
  }

  return out;
}

// ── Project-level aggregator ──────────────────────────────────────

/** Run every per-cabinet check + every cross-cabinet joint check.
 *  Returned issues carry `cabinetId` for surface routing. */
export function validateProject(cabinets: Cabinet[], catalog: CatalogIndex): Issue[] {
  const out: Issue[] = [];
  for (const c of cabinets) {
    const issues = validateCabinet(c, catalog);
    for (const i of issues) {
      out.push({ ...i, cabinetId: c._id });
    }
  }
  for (const pair of adjacentPairs(cabinets)) {
    out.push(...checkJoint(pair, catalog));
  }
  return out;
}

/** Convenience: derive aggregate counts for the cabinet's `cached`
 *  field. Called on save so cached metadata reflects post-override
 *  reality and the server `after_*` triggers can roll the per-cabinet
 *  BOM up into `project.totals` without re-running the geometry. */
export function deriveCabinetCache(cab: Cabinet, catalog: CatalogIndex): {
  partCount: number;
  drillingCount: number;
  issueCount: number;
  computedAt: string;
  sheetAreaByMaterial: Record<string, number>;
  edgeLftByBand:       Record<string, number>;
  hardwareByModel:     Record<string, number>;
} {
  const parts = partsFor(cab, catalog);
  const partCount = parts.reduce((acc, p) => acc + (p.qty ?? 1), 0);
  let drillingCount = 0;
  for (const p of parts) {
    drillingCount += effectiveDrillings(cab, p.id).length * (p.qty ?? 1);
  }
  const issueCount = validateCabinet(cab, catalog).filter((i) => i.severity === "error").length;

  // Per-cabinet BOM maps — same logic as lib/cabinets/bom.ts::cabinetBom
  // but flattened to plain objects (FieldMap-friendly). Keys are stringified
  // catalog ids; the server-side aggregator parses them back to int.
  const sheetAreaByMaterial: Record<string, number> = {};
  const edgeLftByBand:       Record<string, number> = {};
  const hardwareByModel:     Record<string, number> = {};
  const edgeBand   = cab.overrides.edgeBandId        ?? catalog.defaults.edgeBandId;
  const hingeId    = cab.overrides.hingeId           ?? catalog.defaults.hingeId;
  const pullId     = cab.overrides.pullId            ?? catalog.defaults.pullId;
  const shelfPinId = catalog.defaults.shelfPinId;
  const addQty = (m: Record<string, number>, id: Id | null | undefined, q: number) => {
    if (id == null || q === 0) return;
    const k = String(id);
    m[k] = (m[k] ?? 0) + q;
  };
  for (const p of parts) {
    const door  = isDoorPart(p);
    const shelf = isShelfPart(p);
    if (p.materialId !== 0) {
      addQty(sheetAreaByMaterial, p.materialId, p.w * p.h * (p.qty ?? 1));
    }
    if (edgeBand != null) {
      // Same banding rules as cabinetBom: doors all 4, shelves/sides/
      // bottom/stretchers front edge only, back+toe kick none.
      let lft = 0;
      if (door)             lft = (2 * p.w + 2 * p.h) / 12;
      else if (shelf)       lft = p.w / 12;
      else if (isSidePart(p))                          lft = p.h / 12;
      else if (isBottomPart(p) || isStretcherPart(p))  lft = p.w / 12;
      if (lft > 0) addQty(edgeLftByBand, edgeBand, lft);
    }
    if (door && hingeId != null) {
      const hingesPerDoor = p.h <= 42 ? 2 : 3;
      addQty(hardwareByModel, hingeId, hingesPerDoor);
    }
    if (door && pullId != null) addQty(hardwareByModel, pullId, 1);
    if (shelf && shelfPinId != null) addQty(hardwareByModel, shelfPinId, 4);
  }

  return {
    partCount,
    drillingCount,
    issueCount,
    computedAt: new Date().toISOString(),
    sheetAreaByMaterial,
    edgeLftByBand,
    hardwareByModel,
  };
}
